<template>
  <div
    class="absolute max-h-[80vh] h-full max-w-[1124px] mx-auto px-4 top-[74px] right-0 size-full !font-normal"
    v-show="props.isOpen"
  >
    <div
      class="card !w-full !max-w-[1124px] ml-auto bg-white dark:bg-[#3f1f56c3] !backdrop-blur-2xl !rounded-[32px] border border-[#9857C81A] overflow-hidden"
    >
      <div class="flex max-h-[80vh]">
        <ul
          class="w-[336px] bg-[#F0E5FF] dark:!bg-[#6B259E33] p-8 space-y-2 overflow-y-auto no-scrollbar"
        >
          <li v-for="item in items" :key="item.label">
            <NuxtLink
              :to="item.link"
              @mouseenter="selectedRootItem = item"
              :class="[
                'flex items-center cursor-pointer px-4 py-2 mb-2 overflow-hidden relative rounded-full text-[#9A4EFF] dark:text-[#BD8DFD]',
                item === selectedRootItem
                  ? 'bg-white dark:bg-[#FFFFFF0D] font-medium dark:font-semibold text-black dark:text-white'
                  : '',
              ]"
            >
              <WrSVGIcon :name="item.icon" class="mr-4 size-6" />
              <span>{{ item.label }}</span>
            </NuxtLink>
          </li>
        </ul>

        <div
          class="flex-1 bg-white dark:bg-[#8442B41A] dark:text-white/80 size-full px-8 pt-6 pb-16 overflow-y-auto flex flex-col max-h-[80vh] min-h-[600px] h-full"
        >
          <WrMenuContent
            v-if="selectedRootItem"
            :items="selectedRootItem.items[0][0].items[0].content"
            :variant="selectedRootItem.items[0][0].items[0].variant"
            :link="selectedRootItem.items[0][0].items[0].link"
            all-services-link="/ai"
            wide-items
            class="h-full dark:text-white/80"
            @close="handleClose"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { defineAsyncComponent } from "vue";

const WrMenuContent = defineAsyncComponent(
  () => import("~/components/molecules/WrMenuContent.vue"),
);

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(["closeMegamenu"]);

const handleClose = () => {
  emit("closeMegamenu");
  selectedRootItem.value = items.value[0];
};

const selectedRootItem = ref(null);

onMounted(() => {
  if (items.value.length) {
    selectedRootItem.value = items.value[0];
  }
});

const items = ref([
  {
    label: "Workforce",
    icon: "WrAI",
    link: "/ai/workforce",
    root: true,
    items: [
      [
        {
          items: [
            {
              comp: "WrMenuContent",
              content: [
                {
                  items: [
                    {
                      label: "AI Sales Employees",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-sales-employees",
                      link: "/ai/workforce/ai-sales-employees",
                    },
                    {
                      label: "AI Marketing Employees",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-marketing-employees",
                      link: "/ai/workforce/ai-marketing-employees",
                    },
                    {
                      label: "AI Support Employees",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-support-employees",
                      link: "/ai/workforce/ai-support-employees",
                    },
                    {
                      label: "AI Operations Employees",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-operations-employees",
                      link: "/ai/workforce/ai-operations-employees",
                    },
                    {
                      label: "AI Internal Assistants",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-internal-assistants",
                      link: "/ai/workforce/ai-internal-assistants",
                    },
                    {
                      label: "Lifecycle Marketing Agent",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "lifecycle-marketing-agent",
                      link: "/ai/workforce/lifecycle-marketing-agent",
                    },
                    {
                      label: "CRM Enrichment Agent",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "crm-enrichment-agent",
                      link: "/ai/workforce/crm-enrichment-agent",
                    },
                    {
                      label: "Inbound Qualification Agent",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "inbound-qualification-agent",
                      link: "/ai/workforce/inbound-qualification-agent",
                    },
                    {
                      label: "Inbox Management Agent",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "inbox-management-agent",
                      link: "/ai/workforce/inbox-management-agent",
                    },
                    {
                      label: "SEO Agent",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "seo-agent",
                      link: "/ai/workforce/seo-agent",
                    },
                    {
                      label: "AI MRP Agents",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "mrp-agent",
                      link: "/ai/workforce/mrp-agent",
                    },
                    {
                      label: "Sales Note-taking Agent",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "sales-note-taking-agent",
                      link: "/ai/workforce/sales-note-taking-agent",
                    },
                    {
                      label: "AI BDR Agent",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-bdr-agent",
                      link: "/ai/workforce/ai-bdr-agent",
                    },
                    {
                      label: "Account Research Agent",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "account-research-agent",
                      link: "/ai/workforce/account-research-agent",
                    },
                    {
                      label: "Autonomous AI Systems",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "autonomous-ai-systems",
                      link: "/ai/workforce/autonomous-ai-systems",
                    },
                    {
                      label: "AI Agent Orchestration",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-agent-orchestration",
                      link: "/ai/workforce/ai-agent-orchestration",
                    },
                    {
                      label: "Multi-Agent Systems",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "multi-agent-systems",
                      link: "/ai/workforce/multi-agent-systems",
                    },
                    {
                      label: "Human + AI Collaboration",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "human-ai-collaboration",
                      link: "/ai/workforce/human-ai-collaboration",
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    ],
  },
  {
    label: "Capabilities",
    icon: "WrAI",
    link: "/ai/capabilities",
    root: true,
    items: [
      [
        {
          items: [
            {
              comp: "WrMenuContent",
              content: [
                {
                  items: [
                    {
                      label: "Agentic AI Development",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "agentic-ai-development",
                      link: "/ai/capabilities/agentic-ai-development",
                    },
                    {
                      label: "AI Infrastructure & Resource Management",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-infrastructure-resource-management",
                      link: "/ai/capabilities/ai-infrastructure-resource-management",
                    },
                    {
                      label: "AI Integrations & API Architecture",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-integrations",
                      link: "/ai/capabilities/ai-integrations",
                    },
                    {
                      label: "AI UX & Interaction Design",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-ux-interaction-design",
                      link: "/ai/capabilities/ai-ux-interaction-design",
                    },
                    {
                      label: "Advanced Data Visualization",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "advanced-data-visualization",
                      link: "/ai/capabilities/advanced-data-visualization",
                    },
                    {
                      label: "Conversational AI",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "conversational-ai",
                      link: "/ai/capabilities/conversational-ai",
                    },
                    {
                      label: "Data Engineering",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "data-engineering",
                      link: "/ai/capabilities/data-engineering",
                    },
                    {
                      label: "Data Strategy & Architecture",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "data-strategy-architecture",
                      link: "/ai/capabilities/data-strategy-architecture",
                    },
                   {
                      label: "Generative AI",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "generative-ai",
                      link: "/ai/capabilities/generative-ai",
                    },
                    {
                      label: "LLM Applications",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "llm-applications",
                      link: "/ai/capabilities/llm-applications",
                    },
                    {
                      label: "Machine Learning",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "machine-learning",
                      link: "/ai/capabilities/machine-learning",
                    },
                    {
                      label: "MLOps & Lifecycle Management",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "mlops",
                      link: "/ai/capabilities/mlops",
                    },
                    {
                      label: "Multi-Agent Orchestration",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "multi-agent-orchestration",
                      link: "/ai/capabilities/multi-agent-orchestration",
                    },
                    {
                      label: "Real-Time AI Analytics",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "real-time-ai-analytics",
                      link: "/ai/capabilities/real-time-ai-analytics",
                    },
                    {
                      label: "Vector Databases & Semantic Search",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "vector-databases",
                      link: "/ai/capabilities/vector-databases",
                    },
                    {
                      label: "Workflow Orchestration Systems",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "workflow-orchestration-systems",
                      link: "/ai/capabilities/workflow-orchestration-systems",
                    },
                    {
                      label: "Data Security",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "data-security",
                      link: "/ai/capabilities/data-security",
                    },
                    {
                      label: "DataOps",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "dataops",
                      link: "/ai/capabilities/dataops",
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    ],
  },
  {
    label: "Products",
    icon: "WrAI",
    link: "/ai/products",
    root: true,
    items: [
      [
        {
          items: [
            {
              comp: "WrMenuContent",
              content: [
                {
                  items: [
                    {
                      label: "AI Dashboard",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-dashboard",
                      link: "/ai/products/ai-dashboard",
                    },
                    {
                      label: "AI Marketplaces",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-marketplaces",
                      link: "/ai/products/ai-marketplaces",
                    },
                    {
                      label: "AI Orchestration Platform",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-orchestration-platform",
                      link: "/ai/products/ai-orchestration-platform",
                    },
                    {
                      label: "AI Voice Agents",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-voice-agents",
                      link: "/ai/products/ai-voice-agents",
                    },
                    {
                      label: "AI Workflow Engines",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-workflow-engines",
                      link: "/ai/products/ai-workflow-engines",
                    },
                    {
                      label: "Developer Copilot",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "developer-copilot",
                      link: "/ai/products/developer-copilot",
                    },
                    {
                      label: "Internal AI Platforms",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "internal-ai-platforms",
                      link: "/ai/products/internal-ai-platforms",
                    },
                    {
                      label: "SaaS AI Products",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "saas-ai-products",
                      link: "/ai/products/saas-ai-products",
                    },
                    {
                      label: "Sales Copilot",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "sales-copilot",
                      link: "/ai/products/sales-copilot",
                    },
                    {
                      label: "Support Copilot",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "support-copilot",
                      link: "/ai/products/support-copilot",
                    },
                    {
                      label: "AI Chatbot Systems",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-chatbot-systems",
                      link: "/ai/products/ai-chatbot-systems",
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    ],
  },
  {
    label: "Services",
    icon: "WrAI",
    link: "/ai/services",
    root: true,
    items: [
      [
        {
          items: [
            {
              comp: "WrMenuContent",
              content: [
                {
                  items: [
                    {
                      label: "Agent-as-a-Service (AaaS)",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "agent-as-a-service",
                      link: "/ai/services/agent-as-a-service",
                    },
                    {
                      label: "AI Agent Development Company",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-agent-development-company",
                      link: "/ai/services/ai-agent-development-company",
                    },
                    {
                      label: "AI Agent Integration Services",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-agent-integration-services",
                      link: "/ai/services/ai-agent-integration-services",
                    },
                    {
                      label: "AI Automation Services",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-automation-services",
                      link: "/ai/services/ai-automation-services",
                    },
                    {
                      label: "AI Copilot Development",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-copilot-development",
                      link: "/ai/services/ai-copilot-development",
                    },
                    {
                      label: "AI Development & Integration",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-development-and-integration",
                      link: "/ai/services/ai-development-and-integration",
                    },
                    {
                      label: "AI Strategy Consulting",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-strategy-consulting",
                      link: "/ai/services/ai-strategy-consulting",
                    },
                    {
                      label: "RAG Development Services",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "rag-development",
                      link: "/ai/services/rag-development",
                    },
                    {
                      label: "Multi-Agent Systems Development",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "multi-agent-systems-development",
                      link: "/ai/services/multi-agent-systems-development",
                    },
                    {
                      label: "Enterprise AI Consulting",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "enterprise-ai-consulting",
                      link: "/ai/services/enterprise-ai-consulting",
                    },
                    {
                      label: "Enterprise AI Development",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "enterprise-ai-development",
                      link: "/ai/services/enterprise-ai-development",
                    },
                    {
                      label: "AI Voice Agent Development",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-voice-agent-development",
                      link: "/ai/services/ai-voice-agent-development",
                    },
                    {
                      label: "Data Engineering Services",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "data-engineering-services",
                      link: "/ai/services/data-engineering-services",
                    },
                    {
                      label: "Computer Vision Development",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "computer-vision-development",
                      link: "/ai/services/computer-vision-development",
                    },
                    {
                      label: "Predictive Analytics & Forecasting",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "predictive-analytics-and-forecasting",
                      link: "/ai/services/predictive-analytics-and-forecasting",
                    },
                    {
                      label: "Custom AI Agent Development",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "custom-ai-agent-development",
                      link: "/ai/services/custom-ai-agent-development",
                    },
                    {
                      label: "Custom AI Model Development",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "custom-ai-model-development",
                      link: "/ai/services/custom-ai-model-development",
                    },
                    {
                      label: "Deep Learning Solutions",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "deep-learning-solutions",
                      link: "/ai/services/deep-learning-solutions",
                    },
                    {
                      label: "Intelligent Workflow Automation",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "intelligent-workflow-automation",
                      link: "/ai/services/intelligent-workflow-automation",
                    },
                    {
                      label: "Machine Learning Development",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "machine-learning-development",
                      link: "/ai/services/machine-learning-development",
                    },
                    {
                      label: "MLOps Engineering Services",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "mlops-services",
                      link: "/ai/services/mlops-services",
                    },
                    {
                      label: "Model Governance & Compliance",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "model-governance-and-compliance",
                      link: "/ai/services/model-governance-and-compliance",
                    },
                    {
                      label: "Multi-Modal AI Development",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "multi-modal-ai-development",
                      link: "/ai/services/multi-modal-ai-development",
                    },
                    {
                      label: "NLP Development Services",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "nlp-development-services",
                      link: "/ai/services/nlp-development-services",
                    },
                    {
                      label: "AI Transformer Model Development",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-transformer-model-development",
                      link: "/ai/services/ai-transformer-model-development",
                    },
                    {
                      label: "Model Deployment & Monitoring",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "model-deployment-and-monitoring",
                      link: "/ai/services/model-deployment-and-monitoring",
                    },
                    {
                      label: "AI Agent Orchestration Services",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-agent-orchestration",
                      link: "/ai/services/ai-agent-orchestration",
                    },
                    {
                      label: "AI Software Development",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-software-development",
                      link: "/ai/services/ai-software-development",
                    },
                    {
                      label: "AI Chatbot Development",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-chatbot-development",
                      link: "/ai/services/ai-chatbot-development",
                    },
                    {
                      label: "AI Conversational Development",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-conversational-development",
                      link: "/ai/services/ai-conversational-development",
                    },
                    {
                      label: "AI OpenAI Integration Services",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-openai-integration-services",
                      link: "/ai/services/ai-openai-integration-services",
                    },
                    {
                      label: "AI Generative AI Development",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-genai-development",
                      link: "/ai/services/ai-genai-development",
                    },
                    {
                      label: "LLM Optimization Services",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "llm-optimization-services",
                      link: "/ai/services/llm-optimization-services",
                    },
                    {
                      label: "LLM Application Development",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "llm-application-development",
                      link: "/ai/services/llm-application-development",
                    },
                    {
                      label: "Assisted Video Object Tracking",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "assisted-video-object-tracking",
                      link: "/ai/services/assisted-video-object-tracking",
                    }
                  ],
                },
              ],
            },
          ],
        },
      ],
    ],
  },
  {
    label: "Solutions",
    icon: "WrAI",
    link: "/ai/solutions",
    root: true,
    items: [
      [
        {
          items: [
            {
              comp: "WrMenuContent",
              content: [
                {
                  items: [
                    {
                      label: "AI for Sales",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-for-sales",
                      link: "/ai/solutions/ai-for-sales",
                    },
                    {
                      label: "Lead Generation Automation",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "lead-generation-automation",
                      link: "/ai/solutions/lead-generation-automation",
                    },
                    {
                      label: "AI Sales Automation",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-sales-automation",
                      link: "/ai/solutions/ai-sales-automation",
                    },
                    {
                      label: "Customer Support Automation",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "customer-support-automation",
                      link: "/ai/solutions/customer-support-automation",
                    },
                     {
                      label: "Back Office Automation",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "back-office-automation",
                      link: "/ai/solutions/back-office-automation",
                    },
                    {
                      label: "AI-powered Analytics & Dashboards",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-powered-analytics-dashboards",
                      link: "/ai/solutions/ai-powered-analytics-dashboards",
                    },
                    {
                      label: "Process Automation",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "process-automation",
                      link: "/ai/solutions/process-automation"
                    },
                    {
                      label: "Document Processing Automation",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "document-processing-automation",
                      link: "/ai/solutions/document-processing-automation"
                    },
                    {
                      label: "AI for Marketing & Content Creation",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-for-marketing-content-creation",
                      link: "/ai/solutions/ai-for-marketing-content-creation"
                    },
                    {
                      label: "AI Personalization Engines",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-personalization-engines",
                      link: "/ai/solutions/ai-personalization-engines"
                    },
                    {
                      label: "AI for HR Automation",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-for-hr-automation",
                      link: "/ai/solutions/ai-for-hr-automation"
                    },
                    {
                      label: "AI Knowledge Assistants",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-knowledge-assistants",
                      link: "/ai/solutions/ai-knowledge-assistants"
                    },
                    {
                      label: "Reporting Automation",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "reporting-automation",
                      link: "/ai/solutions/reporting-automation"
                    },
                    {
                      label: "Decision Support Systems",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "decision-support-systems",
                      link: "/ai/solutions/decision-support-systems"
                    },
                    {
                      label: "Conversational AI Solutions",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "conversational-ai-solutions",
                      link: "/ai/solutions/conversational-ai-solutions"
                    },
                    {
                      label: "AI Workflow Orchestration",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-workflow-orchestration",
                      link: "/ai/solutions/ai-workflow-orchestration"
                    },
                    {
                      label: "AI Business Process Optimization",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-business-process-optimization",
                      link: "/ai/solutions/ai-business-process-optimization"
                    }
                  ],
                },
              ],
            },
          ],
        },
      ],
    ],
  },
  {
    label: "Lab",
    icon: "WrAI",
    link: "/ai/lab",
    root: true,
    items: [
      [
        {
          items: [
            {
              comp: "WrMenuContent",
              content: [
                {
                  items: [
                    {
                      label: "Experiments",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "experiments",
                      link: "/ai/lab/experiments",
                    },
                    {
                      label: "Internal AI Tools",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "internal-ai-tools",
                      link: "/ai/lab/internal-ai-tools",
                    },
                    {
                      label: "Live Agents",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "live-agents",
                      link: "/ai/lab/live-agents",
                    },
                    {
                      label: "AI Research & Insights",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-research-and-insights",
                      link: "/ai/lab/ai-research-and-insights",
                    },
                    {
                      label: "AI Audit & System Optimisation",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-audit-and-system-optimisation",
                      link: "/ai/lab/ai-audit-and-system-optimisation",
                    },
                     {
                      label: "Prototypes & Demos",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "prototypes-and-demos",
                      link: "/ai/lab/prototypes-and-demos",
                    },
                    {
                      label: "AI Enhancement & Performance Tuning",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-enhancement-and-performance-tuning",
                      link: "/ai/lab/ai-enhancement-and-performance-tuning",
                    },
                    {
                      label: "AI Future of Work",
                      iconPosition: "bg-[position:0px_0px]",
                      item: "ai-future-of-work",
                      link: "/ai/lab/ai-future-of-work",
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    ],
  },
]);
</script>
